'use client';

import { useEffect } from 'react';
import { useFontPrefsStore } from '@/shared/stores/font-prefs-store';
import { FONT_PRESETS, FONT_SIZE_PRESETS, FONT_LINK_ID } from '@/shared/constants/fonts';

/**
 * Montat o singură dată în providers.tsx. Citește fontFamily și fontSize
 * din font-prefs-store și aplică:
 *   - CSS var `--font-family` (folosit în globals.css pe body)
 *   - CSS var `--font-base-size-pct` (folosit pe <html> pentru a stabili
 *     valoarea lui 1rem; toate font-size-urile din UI scalează)
 *   - <link> dinamic spre Google Fonts pentru fontul ales (cu cleanup la
 *     schimbare ca să nu acumulăm link-uri vechi).
 *
 * Bootstrap-ul inline din layout.tsx face aceleași lucruri dar înainte de
 * hydration ca să prevenim flash-ul. ThemeApplier-ul acoperă schimbările
 * runtime din UI.
 */
export function FontApplier() {
  const fontFamily = useFontPrefsStore((s) => s.fontFamily);
  const fontSize = useFontPrefsStore((s) => s.fontSize);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const preset = FONT_PRESETS[fontFamily];
    const sizePreset = FONT_SIZE_PRESETS[fontSize];
    if (!preset || !sizePreset) return;

    const root = document.documentElement;
    root.style.setProperty('--font-family', preset.fontStack);
    root.style.setProperty('--font-base-size-pct', sizePreset.pct);
    root.setAttribute('data-font-size', fontSize); // pentru CSS conditional pe preset (ex: hide inline icons la xl)

    const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
    if (preset.googleFontsHref) {
      if (existing) {
        if (existing.href !== preset.googleFontsHref) existing.href = preset.googleFontsHref;
      } else {
        const link = document.createElement('link');
        link.id = FONT_LINK_ID;
        link.rel = 'stylesheet';
        link.href = preset.googleFontsHref;
        document.head.appendChild(link);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [fontFamily, fontSize]);

  return null;
}
